import {
    Button,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import InventoryItemFormBody from '../components/inventory/InventoryItemFormBody';
import {
    InventoryItemFormState,
    emptyInventoryItemForm,
    countMissingInventoryItemFields,
} from '../components/inventory/inventoryItemFormShared';
import { TypeaheadOption } from '../components/inventory/InventoryTypeaheadField';
import { useCreateInventoryBin } from '../hooks/inventory/useCreateInventoryBin';
import { useCreateInventoryTag } from '../hooks/inventory/useCreateInventoryTag';
import { useGetInventoryUnits } from '../hooks/inventory/useGetInventoryUnits';
import { useUploadInventoryImage } from '../hooks/inventory/useUploadInventoryImage';
import {
    MovePartToInventoryEntry,
    useMovePartsToInventory,
} from '../hooks/useMovePartsToInventory';
import { useGetLocations, filterLocations } from '../hooks/useGetLocations';
import { useSearchSuppliers } from '../hooks/useSearchSuppliers';
import { useGetSettings } from '../hooks/useGetSettings';
import {
    InventoryBin,
    InventoryFormReferenceField,
    InventoryTag,
} from '../types/inventory';
import {
    parseInventoryReferenceValidationError,
    referenceValidationFieldToFormKey,
} from '../utils/inventoryConstants';
import { appendTagIfMissing } from '../utils/inventoryTags';

export interface MoveToInventoryPartSnapshot {
    partId: string;
    name: string;
    partNumber: string;
    quantity: number;
    buyPrice: number;
    vendorLabel?: string;
}

interface MoveToInventoryConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBackToActions: () => void;
    onSuccess: () => void;
    supplierOrderId: string;
    parts: Array<MoveToInventoryPartSnapshot>;
    locationId: string;
    roNumber: string;
    vehicleMake?: string;
}

const MoveToInventoryConfirmModal = ({
    isOpen,
    onClose,
    onBackToActions,
    onSuccess,
    supplierOrderId,
    parts,
    locationId,
    roNumber,
    vehicleMake = '',
}: MoveToInventoryConfirmModalProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [pageIndex, setPageIndex] = useState(0);
    const [submittedPartIds, setSubmittedPartIds] = useState<Set<string>>(() => new Set());
    const [drafts, setDrafts] = useState<Array<InventoryItemFormState>>([]);
    const [extras, setExtras] = useState<
        Array<{
            tags: Array<InventoryTag>;
            bins: Array<InventoryBin>;
            imagePreviewUrl: string | null;
            imageKey: string | undefined;
            photoAndTagsOpen: boolean;
            detailsOpen: boolean;
            supplierSearch: string;
            locationSearch: string;
            fieldErrors: Partial<Record<InventoryFormReferenceField, string>>;
        }>
    >([]);

    const { data: locationsData } = useGetLocations(isOpen);
    const { data: settingsData } = useGetSettings();
    const uploadImageMutation = useUploadInventoryImage();
    const createTagMutation = useCreateInventoryTag();
    const createBinMutation = useCreateInventoryBin();
    const moveMutation = useMovePartsToInventory();

    const defaultBusinessLine =
        settingsData?.inventory?.defaultBusinessLine?.trim() ?? '';

    const { data: allSuppliersData, isLoading: isSupplierLoading } = useSearchSuppliers('', {
        listAll: isOpen,
    });
    const {
        data: unitsData,
        isLoading: isUnitsLoading,
        isError: isUnitsError,
    } = useGetInventoryUnits(isOpen);
    const allowedUnits = unitsData?.units ?? [];

    useEffect(() => {
        if (!isOpen || parts.length === 0) return;
        setPageIndex(0);
        setSubmittedPartIds(new Set());
        setDrafts(
            parts.map((p) =>
                emptyInventoryItemForm(locationId, {
                    manufacturerPartNumber: p.partNumber,
                    name: p.name,
                    category: 'part',
                    unit: 'each',
                    unitCost: p.buyPrice,
                    inStockQuantity: Math.max(1, p.quantity),
                    reorderQuantity: 0,
                    targetQuantity: 0,
                    origin: roNumber,
                    businessLine: defaultBusinessLine,
                    locationId,
                }),
            ),
        );
        const makeTag = vehicleMake.trim();
        setExtras(
            parts.map((p) => ({
                tags: makeTag ? appendTagIfMissing([], makeTag) : [],
                bins: [],
                imagePreviewUrl: null,
                imageKey: undefined,
                photoAndTagsOpen: false,
                detailsOpen: false,
                supplierSearch: p.vendorLabel ?? '',
                locationSearch: '',
                fieldErrors: {},
            })),
        );
    }, [isOpen, parts, locationId, roNumber, defaultBusinessLine, vehicleMake]);

    // Resolve supplier id from vendor label once suppliers load.
    useEffect(() => {
        if (!isOpen || !allSuppliersData?.suppliers?.length) return;
        setDrafts((prev) =>
            prev.map((draft, i) => {
                if (draft.supplierId) return draft;
                const label = (extras[i]?.supplierSearch || parts[i]?.vendorLabel || '')
                    .trim()
                    .toLowerCase();
                if (!label) return draft;
                const match = allSuppliersData.suppliers.find(
                    (s) => s.title.trim().toLowerCase() === label,
                );
                return match ? { ...draft, supplierId: match._id } : draft;
            }),
        );
    }, [isOpen, allSuppliersData?.suppliers, extras, parts]);

    const currentPart = parts[pageIndex];
    const form = drafts[pageIndex];
    const extra = extras[pageIndex];
    const isLast = pageIndex >= parts.length - 1;
    const maxQty = currentPart?.quantity ?? 1;

    const selectableLocations = useMemo(
        () => filterLocations(locationsData?.locations ?? [], { excludeAll: true }),
        [locationsData?.locations],
    );
    const locationOptions: TypeaheadOption[] = useMemo(
        () => selectableLocations.map((loc) => ({ value: loc._id, label: loc.title })),
        [selectableLocations],
    );

    const [supplierSearchLocal, setSupplierSearchLocal] = useState('');
    useEffect(() => {
        setSupplierSearchLocal(extra?.supplierSearch ?? '');
    }, [pageIndex, extra?.supplierSearch]);

    const supplierOptions: TypeaheadOption[] = useMemo(() => {
        const all = allSuppliersData?.suppliers ?? [];
        const q = supplierSearchLocal.trim().toLowerCase();
        return all
            .filter((s) => !q || s.title.toLowerCase().includes(q))
            .map((s) => ({ value: s._id, label: s.title }));
    }, [allSuppliersData?.suppliers, supplierSearchLocal]);

    const supplierDisplay = useMemo(() => {
        if (!form) return supplierSearchLocal;
        const match = supplierOptions.find((o) => o.value === form.supplierId);
        if (match) return match.label;
        return supplierSearchLocal;
    }, [supplierOptions, form, supplierSearchLocal]);

    const locationDisplay = useMemo(() => {
        if (!form) return extra?.locationSearch ?? '';
        const loc = selectableLocations.find((l) => l._id === form.locationId);
        if (loc) return loc.title;
        return extra?.locationSearch ?? '';
    }, [selectableLocations, form, extra?.locationSearch]);

    const updateDraftField = <K extends keyof InventoryItemFormState>(
        key: K,
        value: InventoryItemFormState[K],
    ) => {
        setDrafts((prev) => {
            const next = [...prev];
            const cur = next[pageIndex];
            if (!cur) return prev;
            if (key === 'locationId' && cur.locationId !== value) {
                setExtras((ex) => {
                    const eNext = [...ex];
                    if (eNext[pageIndex]) eNext[pageIndex] = { ...eNext[pageIndex], bins: [] };
                    return eNext;
                });
            }
            next[pageIndex] = { ...cur, [key]: value };
            return next;
        });
        if (key === 'locationId' || key === 'supplierId' || key === 'unit') {
            setExtras((ex) => {
                const eNext = [...ex];
                const curEx = eNext[pageIndex];
                if (!curEx) return ex;
                const fieldErrors = { ...curEx.fieldErrors };
                delete fieldErrors[key as InventoryFormReferenceField];
                eNext[pageIndex] = { ...curEx, fieldErrors };
                return eNext;
            });
        }
    };

    const setFormForPage: React.Dispatch<React.SetStateAction<InventoryItemFormState>> = (
        updater,
    ) => {
        setDrafts((prev) => {
            const next = [...prev];
            const cur = next[pageIndex];
            if (!cur) return prev;
            next[pageIndex] =
                typeof updater === 'function' ? updater(cur) : updater;
            return next;
        });
    };

    const validateCurrentPage = useCallback((): boolean => {
        if (!form || !currentPart) return false;
        const missing = countMissingInventoryItemFields(form, {
            maxInStockQuantity: maxQty,
        });
        if (missing > 0 || !form.supplierId) {
            toast({
                title: 'Missing required fields',
                description: !form.supplierId
                    ? 'Select a supplier from the list.'
                    : 'Complete all required fields before continuing.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            if (!form.supplierId) {
                setExtras((ex) => {
                    const eNext = [...ex];
                    if (eNext[pageIndex]) {
                        eNext[pageIndex] = {
                            ...eNext[pageIndex],
                            fieldErrors: {
                                ...eNext[pageIndex].fieldErrors,
                                supplierId: 'Select a supplier from the list.',
                            },
                        };
                    }
                    return eNext;
                });
            }
            return false;
        }
        if (form.inStockQuantity < 1 || form.inStockQuantity > maxQty) {
            toast({
                title: 'Invalid quantity',
                description: `Quantity to Inventory must be between 1 and ${maxQty}.`,
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return false;
        }
        return true;
    }, [form, currentPart, maxQty, toast, pageIndex]);

    const buildPartPayload = async (
        draft: InventoryItemFormState,
        part: MoveToInventoryPartSnapshot,
        ex: (typeof extras)[number],
    ): Promise<MovePartToInventoryEntry> => {
        const resolvedTagIds: string[] = [];
        for (const t of ex.tags) {
            if (t.isNew) {
                const { tag } = await createTagMutation.mutateAsync({ name: t.name });
                resolvedTagIds.push(tag._id);
            } else {
                resolvedTagIds.push(t._id);
            }
        }
        const resolvedBinIds: string[] = [];
        for (const b of ex.bins) {
            if (b.isNew) {
                const { bin } = await createBinMutation.mutateAsync({
                    locationId: locationId || draft.locationId,
                    name: b.name,
                });
                resolvedBinIds.push(bin._id);
            } else {
                resolvedBinIds.push(b._id);
            }
        }

        return {
            partId: part.partId,
            quantity: draft.inStockQuantity,
            manufacturerPartNumber: draft.manufacturerPartNumber.trim(),
            name: draft.name.trim(),
            partType: draft.partType,
            ...(draft.supplierId ? { supplierId: draft.supplierId } : {}),
            restockAlert: draft.restockAlert,
            reorderQuantity: draft.restockAlert
                ? (draft.reorderQuantity ?? 0)
                : draft.reorderQuantity,
            targetQuantity: draft.restockAlert
                ? (draft.targetQuantity ?? 0)
                : draft.targetQuantity,
            ...(ex.imageKey ? { imageKey: ex.imageKey } : {}),
            ...(resolvedTagIds.length > 0 ? { tagIds: resolvedTagIds } : {}),
            ...(resolvedBinIds.length > 0 ? { binIds: resolvedBinIds } : {}),
        };
    };

    const handleSubmitCurrent = async (): Promise<boolean> => {
        if (!validateCurrentPage()) return false;
        const draft = drafts[pageIndex];
        const ex = extras[pageIndex];
        const part = parts[pageIndex];
        if (!draft || !ex || !part) return false;

        // Already moved this part in this session — skip re-submit.
        if (submittedPartIds.has(part.partId)) return true;

        try {
            const payload = await buildPartPayload(draft, part, ex);
            const { movedPart } = await moveMutation.mutateAsync({
                supplierOrderId,
                part: payload,
            });
            setSubmittedPartIds((prev) => {
                const next = new Set(prev);
                next.add(movedPart.partId);
                return next;
            });
            toast({
                title: 'Part moved to inventory',
                description: part.name || part.partNumber,
                status: 'success',
                duration: 2500,
                isClosable: true,
            });
            // Refetch order so Ordered UI shows split / $0 list lines; inventory feed for stock.
            queryClient.invalidateQueries({ queryKey: ['/order/detail'] });
            queryClient.invalidateQueries({ queryKey: ['/order/drawer'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            return true;
        } catch (error) {
            const referenceError = parseInventoryReferenceValidationError(error);
            if (referenceError) {
                const formKey = referenceValidationFieldToFormKey(referenceError.field);
                setExtras((exState) => {
                    const eNext = [...exState];
                    if (eNext[pageIndex]) {
                        eNext[pageIndex] = {
                            ...eNext[pageIndex],
                            fieldErrors: { [formKey]: referenceError.message },
                        };
                    }
                    return eNext;
                });
            }
            const message = (error as { response?: { data?: { message?: string } } })
                ?.response?.data?.message;
            toast({
                title: 'Failed to move part to inventory',
                description: message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }
    };

    const handleNext = async () => {
        const ok = await handleSubmitCurrent();
        if (!ok) return;
        if (isLast) {
            onSuccess();
            onClose();
            return;
        }
        setPageIndex((i) => i + 1);
    };

    const handleBack = () => {
        if (pageIndex === 0) {
            onBackToActions();
            return;
        }
        setPageIndex((i) => i - 1);
    };

    if (!form || !extra || !currentPart) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            scrollBehavior="inside"
            isCentered
        >
            <ModalOverlay />
            <ModalContent maxH="90vh">
                <ModalHeader>
                    Move to Inventory ({pageIndex + 1} of {parts.length})
                    <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                        {currentPart.name} · {currentPart.partNumber}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody px={5} py={0}>
                    <VStack spacing={0} align="stretch">
                        <InventoryItemFormBody
                            form={form}
                            fieldErrors={extra.fieldErrors}
                            supplierDisplay={supplierDisplay}
                            supplierOptions={supplierOptions}
                            isSupplierLoading={isSupplierLoading}
                            onSupplierSearch={(val) => {
                                setSupplierSearchLocal(val);
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        eNext[pageIndex] = {
                                            ...eNext[pageIndex],
                                            supplierSearch: val,
                                        };
                                    }
                                    return eNext;
                                });
                            }}
                            onSupplierChange={(val) => {
                                const match =
                                    supplierOptions.find((o) => o.value === val) ??
                                    supplierOptions.find((o) => o.label === val);
                                if (match) {
                                    updateDraftField('supplierId', match.value);
                                    setSupplierSearchLocal(match.label);
                                    setExtras((ex) => {
                                        const eNext = [...ex];
                                        if (eNext[pageIndex]) {
                                            eNext[pageIndex] = {
                                                ...eNext[pageIndex],
                                                supplierSearch: match.label,
                                            };
                                        }
                                        return eNext;
                                    });
                                } else {
                                    updateDraftField('supplierId', '');
                                    setSupplierSearchLocal(val);
                                }
                            }}
                            locationDisplay={locationDisplay}
                            locationOptions={locationOptions}
                            onLocationChange={(val) => {
                                const trimmed = val.trim();
                                const match =
                                    locationOptions.find((o) => o.value === val) ??
                                    locationOptions.find((o) => o.label === trimmed);
                                if (match) {
                                    updateDraftField('locationId', match.value);
                                    setExtras((ex) => {
                                        const eNext = [...ex];
                                        if (eNext[pageIndex]) {
                                            eNext[pageIndex] = {
                                                ...eNext[pageIndex],
                                                locationSearch: match.label,
                                            };
                                        }
                                        return eNext;
                                    });
                                } else {
                                    updateDraftField('locationId', '');
                                    setExtras((ex) => {
                                        const eNext = [...ex];
                                        if (eNext[pageIndex]) {
                                            eNext[pageIndex] = {
                                                ...eNext[pageIndex],
                                                locationSearch: val,
                                            };
                                        }
                                        return eNext;
                                    });
                                }
                            }}
                            bins={extra.bins}
                            onBinsChange={(bins) => {
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        eNext[pageIndex] = { ...eNext[pageIndex], bins };
                                    }
                                    return eNext;
                                });
                            }}
                            tags={extra.tags}
                            onTagsChange={(tags) => {
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        eNext[pageIndex] = { ...eNext[pageIndex], tags };
                                    }
                                    return eNext;
                                });
                            }}
                            imagePreviewUrl={extra.imagePreviewUrl}
                            imageKey={extra.imageKey}
                            isImageUploading={uploadImageMutation.isPending}
                            onImageFileSelected={async (file) => {
                                const objectUrl = URL.createObjectURL(file);
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        const prev = eNext[pageIndex].imagePreviewUrl;
                                        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                                        eNext[pageIndex] = {
                                            ...eNext[pageIndex],
                                            imagePreviewUrl: objectUrl,
                                        };
                                    }
                                    return eNext;
                                });
                                try {
                                    const key = await uploadImageMutation.mutateAsync(file);
                                    setExtras((ex) => {
                                        const eNext = [...ex];
                                        if (eNext[pageIndex]) {
                                            eNext[pageIndex] = {
                                                ...eNext[pageIndex],
                                                imageKey: key,
                                            };
                                        }
                                        return eNext;
                                    });
                                } catch {
                                    setExtras((ex) => {
                                        const eNext = [...ex];
                                        if (eNext[pageIndex]) {
                                            const prev = eNext[pageIndex].imagePreviewUrl;
                                            if (prev?.startsWith('blob:')) {
                                                URL.revokeObjectURL(prev);
                                            }
                                            eNext[pageIndex] = {
                                                ...eNext[pageIndex],
                                                imagePreviewUrl: null,
                                                imageKey: undefined,
                                            };
                                        }
                                        return eNext;
                                    });
                                    throw new Error('upload failed');
                                }
                            }}
                            onImageClear={() => {
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        const prev = eNext[pageIndex].imagePreviewUrl;
                                        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                                        eNext[pageIndex] = {
                                            ...eNext[pageIndex],
                                            imagePreviewUrl: null,
                                            imageKey: undefined,
                                        };
                                    }
                                    return eNext;
                                });
                            }}
                            photoAndTagsOpen={extra.photoAndTagsOpen}
                            onPhotoAndTagsOpenChange={(open) => {
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        eNext[pageIndex] = {
                                            ...eNext[pageIndex],
                                            photoAndTagsOpen: open,
                                        };
                                    }
                                    return eNext;
                                });
                            }}
                            detailsOpen={extra.detailsOpen}
                            onDetailsOpenChange={(open) => {
                                setExtras((ex) => {
                                    const eNext = [...ex];
                                    if (eNext[pageIndex]) {
                                        eNext[pageIndex] = {
                                            ...eNext[pageIndex],
                                            detailsOpen: open,
                                        };
                                    }
                                    return eNext;
                                });
                            }}
                            updateField={updateDraftField}
                            setForm={setFormForPage}
                            maxInStockQuantity={maxQty}
                            isUnitsLoading={isUnitsLoading}
                            isUnitsError={isUnitsError}
                            unitCostReadOnly
                        />
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3} w="100%" justify="space-between">
                        <Button
                            size="action"
                            variant="outlineGray"
                            onClick={handleBack}
                            isDisabled={moveMutation.isPending}
                        >
                            Back
                        </Button>
                        <Button
                            size="action"
                            onClick={() => void handleNext()}
                            isLoading={moveMutation.isPending}
                            loadingText={isLast ? 'Finishing…' : 'Saving…'}
                            isDisabled={
                                isUnitsLoading ||
                                isUnitsError ||
                                allowedUnits.length === 0 ||
                                uploadImageMutation.isPending
                            }
                        >
                            {isLast ? 'Finish' : 'Next'}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default MoveToInventoryConfirmModal;
